import sortBy from 'lodash/sortBy'
import { FieldNamesMarkedBoolean } from 'react-hook-form'

import { CustomFieldKind } from '@avantstay/arriere-clients/dist/arriereBackoffice'

import { CustomFieldProps } from '@new/__global/components/CustomField/_types'
import customFieldsDefinitions from '@new/__global/components/CustomField/types'
import { CustomFieldProjectionView } from '@new/__global/components/CustomFieldSection/_types'

import { isFieldEmpty } from '@utils/customFields'

export const getVerifiedAndDirtyFields = ({
  customFields,
  dirtyCustomFields = [],
}: {
  customFields?: CustomFieldProjectionView[]
  dirtyCustomFields?: Partial<Readonly<FieldNamesMarkedBoolean<CustomFieldProps[]>>>
}) => {
  const parsedCustomFieldsValues = dirtyCustomFields?.reduce(
    (prev: { [key: string]: unknown }, _, index: number) => {
      const currentValue = customFields?.[index]
      return Object.assign(prev, { [currentValue?.id || '']: currentValue?.value })
    },
    {},
  )

  // This is used to get all verified fields that were not changed in the form
  const verifiedFieldsValues = customFields
    ?.filter(fieldVerified => fieldVerified.isVerified)
    .reduce((prev, curr) => {
      const currentItem = customFields?.find(field => field.name === curr.name)
      if (currentItem) return Object.assign(prev, { [currentItem.id]: currentItem.value })
      return prev
    }, {})

  const fieldIdValues = {
    ...verifiedFieldsValues,
    ...parsedCustomFieldsValues,
  } as { [id: string]: unknown }[]

  const names = Object.keys(fieldIdValues).map(
    fieldId => customFields?.find(field => field.id === fieldId)?.name,
  ) as string[]

  return {
    fieldIdValues,
    names,
  }
}

export const getCustomFieldsOrdered = (customFields?: CustomFieldProjectionView[]) => {
  if (customFields) {
    // Separate the array based on whether 'value' is defined
    const withoutValue = customFields?.filter(field => isFieldEmpty(field.value))
    const withValue = customFields?.filter(
      field => !isFieldEmpty(field.value) && !withoutValue.includes(field),
    )

    // Sort both groups alphabetically by 'name'
    const fieldsSortedWithValue = sortBy(withValue, 'name')
    const fieldsSortedWithoutValue = sortBy(withoutValue, 'name')

    return {
      fieldsSortedWithValue,
      fieldsSortedWithoutValue,
    }
  }

  return {}
}

export const parseCustomFieldDefaultValue = (customField: CustomFieldProps) => {
  switch (customField.kind) {
    case CustomFieldKind.dateTime:
    case CustomFieldKind.timeRange:
    case CustomFieldKind.number:
    case CustomFieldKind.boolean:
    case CustomFieldKind.time:
    case CustomFieldKind.money:
    case CustomFieldKind.date:
      return customFieldsDefinitions[customField.kind].parseValueToShow?.(customField)
    default:
      if (customField.value && customField.value !== '""' && customField.value !== 'null') {
        try {
          return JSON.parse(customField.value.trim())
        } catch (error) {
          // If JSON parsing fails, return undefined
        }
      }
      return undefined
  }
}
